import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLines } from "./Reveal";

const BlueprintBox = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    <g fill="none" stroke="#f4f5f7" strokeOpacity="0.7" strokeWidth="0.8">
      <path d="M100 20L170 60V140L100 180L30 140V60Z" />
      <path d="M30 60L100 100L170 60" />
      <path d="M100 100V180" stroke="#d96b27" strokeOpacity="1" strokeWidth="1.6" />
      <path d="M65 40L135 80V160" strokeDasharray="3 4" strokeOpacity="0.35" />
      <path d="M135 40L65 80V160" strokeDasharray="3 4" strokeOpacity="0.35" />
    </g>
    <g fontFamily="JetBrains Mono" fontSize="6" fill="#a7aebb">
      <text x="150" y="42">A — 4.80m</text>
      <text x="8" y="104">B — 3.20m</text>
      <text x="104" y="194">Ø 01</text>
    </g>
  </svg>
);

export const HomeHero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      onMouseMove={onMove}
      className="relative min-h-[100svh] overflow-hidden blueprint"
    >
      <motion.div
        initial={{ clipPath: "inset(12% 8% 12% 50%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.8, delay: 0.9, ease: [0.7, 0, 0.2, 1] }}
        className="absolute inset-0"
      >
        <motion.img
          src="/img/hero.jpg"
          alt="Modern architectural interior"
          style={{ y: imgY, scale: imgScale }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0c0e12]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c0e12] via-[#0c0e12]/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0c0e12] to-transparent" />
      </motion.div>

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-10 pt-36 sm:pt-44 pb-16 min-h-[100svh] flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs uppercase tracking-[0.25em] text-[#a7aebb]"
        >
          <span className="text-[#d96b27]">● Preston, Lancashire</span>
          <span>53.7632° N, 2.7031° W</span>
          <span>Architecture + Build</span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-end mt-16">
          <div className="lg:col-span-9">
            <MaskLines
              testId="hero-headline"
              lines={["We design the box.", "Then we build", <span key="x" className="text-[#d96b27]">what goes inside.</span>]}
              className="font-display font-extrabold tracking-[-0.03em] text-[12vw] sm:text-7xl lg:text-[6.5rem] xl:text-[7rem] leading-[0.92]"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-8 max-w-xl text-base md:text-lg text-[#c9ced8] leading-relaxed"
            >
              Interior Box provides architectural and build services for the North West — one team taking your
              project from the first drawing to the final finish.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-start-project-btn"
                className="group inline-flex items-center gap-3 bg-[#d96b27] hover:bg-[#f07d38] text-[#0c0e12] px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] transition-colors clip-frame-sm"
              >
                Start a project
                <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
              </Link>
              <Link
                to="/projects"
                data-testid="hero-view-work-btn"
                className="inline-flex items-center gap-3 border border-[#3a4152] hover:border-[#f4f5f7] px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] transition-colors backdrop-blur-sm"
              >
                View work
              </Link>
            </motion.div>
          </div>

          <div className="hidden lg:block lg:col-span-3" style={{ perspective: 900 }}>
            <motion.div
              data-testid="hero-blueprint-card"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 1.4 }}
              style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
              className="relative aspect-square border border-[#3a4152] bg-[#0c0e12]/60 backdrop-blur-xl p-6"
            >
              <span className="absolute top-3 left-3 font-mono text-[11px] text-[#a7aebb]">DWG / IB-001</span>
              <span className="absolute bottom-3 right-3 font-mono text-[11px] text-[#d96b27]">ISO VIEW</span>
              <div style={{ transform: "translateZ(50px)" }} className="w-full h-full">
                <BlueprintBox />
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mt-16 flex items-center justify-between font-mono text-xs uppercase tracking-[0.25em] text-[#a7aebb]">
          <span className="flex items-center gap-3">
            <ArrowDown className="w-4 h-4 animate-bounce text-[#d96b27]" /> Scroll
          </span>
          <span className="hidden sm:block">Design · Build · Deliver</span>
        </div>
      </div>
    </section>
  );
};
